import React, { useRef, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, StyleSheet, View } from 'react-native';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import InternationalPaymentNotice from '@/components/InternationalPaymentNotice';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import { PRICING_PLANS, PLUS_MONTHLY_CREDITS_DISPLAY, PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY } from '@/constants/config';
import { MODEL_TOTAL } from '@/constants/modelInventory.generated';

export function UpgradeIntroduction() {
  const { t } = useTranslation();
  return <View style={styles.introduction}>
    <Text size="xxl" weight="bold" accessibilityRole="header" accessibilityLevel={2}>{t('settings.upgrade.title')}</Text>
    <Text size="n" color="secondary" style={styles.description}>{t('settings.upgrade.subtitle')}</Text>
  </View>;
}

export function UpgradeBenefits() {
  const { colors } = useTheme();
  const { t } = useTranslation();
  return <View style={[styles.benefits, { borderTopColor: colors.border_color_secondary }]}>
    <Text size="m" weight="bold" accessibilityRole="header" accessibilityLevel={3}>{t('settings.upgrade.included')}</Text>
    <View style={styles.benefitGrid}>
      {[
        { icon: 'briefcase', title: t('settings.upgrade.workTitle'), detail: t('settings.upgrade.workDetail') },
        { icon: 'comment-dots', title: t('settings.upgrade.chatTitle'), detail: t('settings.upgrade.chatDetail') },
        { icon: 'sparkles', title: t('settings.upgrade.createTitle'), detail: t('settings.upgrade.createDetail', { count: MODEL_TOTAL }) },
      ].map(item => <View key={item.icon} style={styles.benefit}>
        <Icon name={item.icon} size={19} color={colors.text_color_primary} />
        <Text weight="medium">{item.title}</Text>
        <Text size="s" color="secondary" style={styles.description}>{item.detail}</Text>
      </View>)}
    </View>
  </View>;
}

function Selectable({ label, selected, disabled, onPress, children, style }) {
  const { colors } = useTheme();
  const [focused, setFocused] = useState(false);
  return <Pressable accessibilityRole="radio" accessibilityLabel={label}
    accessibilityState={{ checked: selected, disabled }} disabled={disabled} onPress={onPress}
    {...(Platform.OS === 'web' ? {
      'aria-checked': selected,
      tabIndex: selected && !disabled ? 0 : -1,
      onKeyDown: event => {
        if (disabled) return;
        if (event.key === ' ' || event.key === 'Spacebar') {
          event.preventDefault();
          onPress();
        } else if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          const options = [...event.currentTarget.closest('[role="radiogroup"]').querySelectorAll('[role="radio"]')];
          const index = options.indexOf(event.currentTarget);
          const step = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1 : (index + step + options.length) % options.length;
          options[next].focus();
          options[next].click();
        }
      },
    } : {})}
    onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
    style={({ hovered, pressed }) => [styles.selectable, style, {
      borderColor: focused ? colors.text_color_link : selected ? colors.text_color_primary : colors.border_color_secondary,
      backgroundColor: selected || hovered ? colors.solid_color_tertiary : colors.solid_color_block,
      opacity: disabled ? 0.65 : pressed ? 0.85 : 1,
    }]}>{children}</Pressable>;
}

// This view owns presentation only. PricingUpgrade retains selection, analytics,
// processor routing and the existing checkout implementation.
export default function UpgradePlanPicker({ tier, cadence, payWithBitcoin, plusPrice, premiumPrice,
  onSelectTier, onSelectCadence, onEnterBitcoin, onExitBitcoin, onUpgrade, isLoading, ctaLabel }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [width, setWidth] = useState(0);
  const continuing = useRef(false);
  const annual = cadence === 'annual';
  const config = tier === 'premium' ? PRICING_PLANS.plusPremium : PRICING_PLANS.plus;
  const savings = Math.round((1 - Number(config.annualPrice) / (Number(config.monthlyPrice) * 12)) * 100);
  const money = value => `$${Number(value).toFixed(2)}`;
  const total = payWithBitcoin ? config.bitcoinAnnualPrice : annual ? config.annualPrice : config.monthlyPrice;
  const continueUpgrade = async () => {
    if (continuing.current || isLoading) return;
    continuing.current = true;
    try { await onUpgrade(); } finally { continuing.current = false; }
  };
  return <View style={styles.page} onLayout={event => setWidth(event.nativeEvent.layout.width)}>
    <UpgradeIntroduction />
    <View style={styles.cadenceRow}>
      {payWithBitcoin ? <Text size="s" color="secondary" style={styles.description}>{t('settings.upgrade.bitcoinAnnual')}</Text> : <>
        <View accessibilityRole={Platform.OS === 'web' ? 'radiogroup' : undefined} accessibilityLabel={t('settings.upgrade.billingPeriod')}
          style={[styles.cadence, { backgroundColor: colors.solid_color_block }]}>
          {['annual', 'monthly'].map(value => <Selectable key={value} label={t(`settings.upgrade.${value}Label`)} selected={cadence === value}
            disabled={isLoading} onPress={() => onSelectCadence(value)} style={styles.cadenceOption}>
            <Text size="s" weight={cadence === value ? 'bold' : 'normal'}>{t(`settings.upgrade.${value}`)}</Text>
          </Selectable>)}
        </View>
        <Text size="xs" color="secondary">{t('settings.upgrade.annualSavings', { percent: savings })}</Text>
      </>}
    </View>

    <View accessibilityRole={Platform.OS === 'web' ? 'radiogroup' : undefined} accessibilityLabel={t('settings.upgrade.choosePlan')} style={styles.plans}>
      {[
        { id: 'plus', name: 'Plus', price: plusPrice, config: PRICING_PLANS.plus, detail: t('settings.upgrade.plusFor'), credits: PLUS_MONTHLY_CREDITS_DISPLAY, sessions: 3 },
        { id: 'premium', name: 'Plus Premium', price: premiumPrice, config: PRICING_PLANS.plusPremium, detail: t('settings.upgrade.premiumFor'), credits: PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY, sessions: 10 },
      ].map(plan => <Selectable key={plan.id} label={`Gab AI ${plan.name}`} selected={tier === plan.id} disabled={isLoading}
        onPress={() => onSelectTier(plan.id)} style={[styles.plan, { flexBasis: width >= 560 ? '45%' : '100%' }]}>
        <View style={styles.planHeading}>
          <Text size="l" weight="bold" style={{ flex: 1 }}>{plan.name}</Text>
          <View style={[styles.radio, { borderColor: colors.text_color_secondary, backgroundColor: tier === plan.id ? colors.text_color_primary : 'transparent' }]}>
            {tier === plan.id && <Icon name="check" size={10} color={colors.solid_color_primary} />}
          </View>
        </View>
        <Text size="s" color="secondary">{plan.detail}</Text>
        <View style={styles.price}>
          <Text size="xxxl" weight="bold" style={{ letterSpacing: -0.8 }}>{money(plan.price)}</Text>
          <Text size="s" color="secondary">{t(payWithBitcoin ? 'settings.upgrade.perYear' : 'settings.upgrade.perMonth')}</Text>
        </View>
        <Text size="xs" color="secondary" style={styles.description}>{payWithBitcoin
          ? t('settings.upgrade.bitcoinBilled')
          : annual ? t('settings.upgrade.billedAnnually', { total: money(plan.config.annualPrice) })
            : t('settings.upgrade.billedMonthly', { total: money(plan.config.monthlyPrice) })}</Text>
        <View style={[styles.planFeatures, { borderTopColor: colors.border_color_secondary }]}>
          {[
            t('settings.upgrade.credits', { count: plan.credits }),
            t('settings.upgrade.sessions', { count: plan.sessions }),
            t(plan.id === 'premium' ? 'settings.upgrade.premiumExtra' : 'settings.upgrade.plusExtra'),
          ].map(feature => <View key={feature} style={styles.feature}>
            <Icon name="check" size={11} color={colors.text_color_secondary} />
            <Text size="s" style={{ flex: 1, lineHeight: 20 }}>{feature}</Text>
          </View>)}
        </View>
      </Selectable>)}
    </View>

    <View style={styles.checkout}>
      <Pressable accessibilityRole="button" accessibilityLabel={ctaLabel}
        accessibilityState={{ busy: isLoading, disabled: isLoading }} disabled={isLoading} onPress={continueUpgrade}
        {...(Platform.OS === 'web' ? { 'aria-busy': isLoading } : {})}
        style={({ hovered, pressed }) => [styles.continueButton, {
          backgroundColor: colors.text_color_primary, opacity: pressed ? 0.8 : hovered || isLoading ? 0.9 : 1,
        }]}>
        {isLoading && <ActivityIndicator size="small" color={colors.solid_color_primary} />}
        <Text weight="bold" style={{ color: colors.solid_color_primary }}>{ctaLabel}</Text>
      </Pressable>
      <Text size="s" color="secondary" style={styles.checkoutNote}>{t(payWithBitcoin ? 'settings.upgrade.bitcoinTotal'
        : annual ? 'settings.upgrade.annualTotal' : 'settings.upgrade.monthlyTotal', { total: money(total) })}</Text>
      <Pressable accessibilityRole="button" accessibilityLabel={t(payWithBitcoin ? 'settings.upgrade.cardInstead' : 'settings.upgrade.bitcoinInstead')}
        disabled={isLoading} onPress={payWithBitcoin ? onExitBitcoin : onEnterBitcoin}
        style={({ hovered }) => [styles.paymentAlternative, hovered && { backgroundColor: colors.solid_color_tertiary }]}>
        <Icon name={payWithBitcoin ? 'credit-card' : 'bitcoin-sign'} size={14} color={colors.text_color_secondary} />
        <Text size="s" color="secondary">{t(payWithBitcoin ? 'settings.upgrade.cardInstead' : 'settings.upgrade.bitcoinInstead')}</Text>
      </Pressable>
    </View>

    <InternationalPaymentNotice />
    <UpgradeBenefits />
    <Text size="xs" color="secondary" style={styles.disclosure}>{t('settings.upgrade.creditNote')}</Text>
  </View>;
}

const styles = StyleSheet.create({
  page: { gap: 22 },
  introduction: { gap: 6 },
  description: { lineHeight: 20 },
  cadenceRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' },
  cadence: { flexDirection: 'row', padding: 3, gap: 3, borderRadius: 13 },
  selectable: { borderWidth: 2, borderRadius: 18 },
  cadenceOption: { minHeight: 42, minWidth: 100, paddingHorizontal: 16, borderRadius: 10, alignItems: 'center', justifyContent: 'center' },
  plans: { flexDirection: 'row', flexWrap: 'wrap', gap: 14, alignItems: 'stretch' },
  plan: { flexGrow: 1, minWidth: 0, padding: 20, gap: 8 },
  planHeading: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  radio: { width: 21, height: 21, borderRadius: 11, borderWidth: 1.5, alignItems: 'center', justifyContent: 'center' },
  price: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'baseline', gap: 5, marginTop: 10 },
  planFeatures: { gap: 10, borderTopWidth: StyleSheet.hairlineWidth, paddingTop: 16, marginTop: 8 },
  feature: { flexDirection: 'row', alignItems: 'center', gap: 9 },
  checkout: { gap: 10, alignItems: 'center' },
  continueButton: { width: '100%', maxWidth: 420, minHeight: 48, padding: 12, borderRadius: 13, flexDirection: 'row', gap: 10, alignItems: 'center', justifyContent: 'center' },
  checkoutNote: { textAlign: 'center', lineHeight: 20 },
  paymentAlternative: { minHeight: 44, paddingHorizontal: 12, borderRadius: 10, flexDirection: 'row', alignItems: 'center', gap: 8 },
  benefits: { borderTopWidth: StyleSheet.hairlineWidth, paddingTop: 24, gap: 20 },
  benefitGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 24 },
  benefit: { flexBasis: 155, flexGrow: 1, gap: 8, minWidth: 0 },
  disclosure: { lineHeight: 18 },
});
